import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { LocationDescriptor } from 'history';
import { RateBillingVariableModel } from '@utmost.co/enterprise-models';
import { useLoggedInUser } from 'src/utils/hooks/useLoggedInUser';
import { FrontdoorNavigator, FrontdoorNavigatorOptions, FrontdoorRoute } from './types';

/**
 * hook that can take you to an existing frontdoor page
 *
 */

export const useFrontdoorRoute = () => {
  const { push, replace } = useHistory();
  const authUser = useLoggedInUser();
  const tenantCode = authUser?.person?.tenantCode ?? 'undefined';

  const createPath = useCallback(
    <STATE extends unknown = undefined, URL_PARAMS extends string[] | undefined = undefined>(
      pathname: string
    ): FrontdoorRoute<STATE, URL_PARAMS> => ({
      path: pathname,
      go: ((options?: FrontdoorNavigatorOptions<STATE, URL_PARAMS>) => {
        push(getLocation(pathname, options));
      }) as FrontdoorNavigator<STATE, URL_PARAMS>,
      replace: ((options?: FrontdoorNavigatorOptions<STATE, URL_PARAMS>) => {
        replace(getLocation(pathname, options));
      }) as FrontdoorNavigator<STATE, URL_PARAMS>
    }),
    [push, replace]
  );

  return useMemo(
    () => ({
      home: createPath(`/${tenantCode}/myutmost`),
      frontdoor: createPath(`/${tenantCode}/frontdoor`),
      addContract: createPath(`/${tenantCode}/frontdoor/add-contract`),
      addContractSelection: createPath(`/${tenantCode}/frontdoor/add-contract/selection`),
      addEngagement: createPath(`/${tenantCode}/frontdoor/add-engagement`),
      addEngagementSelection: createPath(`/${tenantCode}/frontdoor/add-engagement/selection`),
      addRate: createPath(`/${tenantCode}/frontdoor/add-rate`),
      addRateCard: createPath(`/${tenantCode}/frontdoor/add-rate-card`),
      addSupplierContact: createPath(`${tenantCode}/add-supplier-contact`),
      addWorker: createPath(`/${tenantCode}/frontdoor/add-worker`),
      addWorkerSelection: createPath(`/${tenantCode}/frontdoor/add-worker/selection`),
      addWorkRequestSelection: createPath(`/${tenantCode}/frontdoor/add-work-request/selection`),
      editRate: createPath<RateBillingVariableModel>(`/${tenantCode}/frontdoor/edit-rate`),
      pageFlows: createPath<
        {
          originPath?: string;
          originSearch?: string;
          exitPath?: string;
        },
        [uid: string]
      >(`/${tenantCode}/frontdoor/pageflows`),
      updateEngagement: createPath(`/${tenantCode}/frontdoor/update-engagement`),
      updateEngagementSelection: createPath(`/${tenantCode}/frontdoor/update-engagement/selection`),
      workerOffboarding: createPath(`/${tenantCode}/frontdoor/worker-offboarding`),
      workerOffboardingSelection: createPath(`/${tenantCode}/frontdoor/worker-offboarding/selection`)
    }),
    [createPath, tenantCode]
  );
};

function getLocation<STATE, URL_PARAMS extends string[] | undefined>(
  pathname: string,
  options?: FrontdoorNavigatorOptions<STATE, URL_PARAMS>
): LocationDescriptor<STATE> {
  return {
    pathname: (options as FrontdoorNavigatorOptions<STATE, string[]>)?.urlParams?.length
      ? `${pathname}/${(options as FrontdoorNavigatorOptions<STATE, string[]>)?.urlParams.join('/')}`
      : pathname,
    state: (options as any)?.state
  };
}
